import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useState } from 'react'

interface AddExceptionDialogProps {
    isOpen: boolean
    onClose: () => void
    onAdd: (exception: any) => void
}

export function AddExceptionDialog({ isOpen, onClose, onAdd }: AddExceptionDialogProps) {
    const [greetingMessage, setGreetingMessage] = useState('')
    const [greetingLanguage, setGreetingLanguage] = useState('de')
    const [goodbyeMessage, setGoodbyeMessage] = useState('')
    const [validFrom, setValidFrom] = useState('00:00')
    const [validTo, setValidTo] = useState('23:59')
    const [validOnDay, setValidOnDay] = useState<string[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)

    const weekdays = [
        { value: 'MONDAY', label: 'Montag' },
        { value: 'TUESDAY', label: 'Dienstag' },
        { value: 'WEDNESDAY', label: 'Mittwoch' },
        { value: 'THURSDAY', label: 'Donnerstag' },
        { value: 'FRIDAY', label: 'Freitag' },
        { value: 'SATURDAY', label: 'Samstag' },
        { value: 'SUNDAY', label: 'Sonntag' }
    ]

    const handleReset = () => {
        setGreetingMessage('')
        setGreetingLanguage('de')
        setGoodbyeMessage('')
        setValidFrom('00:00')
        setValidTo('23:59')
        setValidOnDay([])
    }

    const handleClose = () => {
        handleReset()
        onClose()
    }

    const toggleDay = (day: string) => {
        setValidOnDay(prev =>
            prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
        )
    }

    const handleAdd = async () => {
        if (!greetingMessage) return

        setIsSubmitting(true)
        try {
            await onAdd({
                utteranceToSpeak: greetingMessage,
                utteranceLanguage: greetingLanguage,
                utteranceAtEnd: goodbyeMessage,
                validFrom,
                validTo,
                validOnDay,
                defaultElement: false,
                possibleSelections: {}
            })
            handleClose()
        } catch (error) {
            console.error('Error adding exception:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const isFormValid = greetingMessage && validOnDay.length > 0

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>Exception hinzufügen</DialogTitle>
                    <DialogDescription>
                        Erstellen Sie eine neue zeitbasierte Ausnahme-Konfiguration
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="greetingMessage">Begrüßungsnachricht *</Label>
                        <Input
                            id="greetingMessage"
                            value={greetingMessage}
                            onChange={(e) => setGreetingMessage(e.target.value)}
                            placeholder="Begrüßungstext"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="greetingLanguage">Sprache</Label>
                        <Select value={greetingLanguage} onValueChange={setGreetingLanguage}>
                            <SelectTrigger id="greetingLanguage">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="de">Deutsch</SelectItem>
                                <SelectItem value="en">English</SelectItem>
                                <SelectItem value="fr">Français</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="goodbyeMessage">Abschiedsnachricht</Label>
                        <Input
                            id="goodbyeMessage"
                            value={goodbyeMessage}
                            onChange={(e) => setGoodbyeMessage(e.target.value)}
                            placeholder="Optional"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="validFrom">Gültig von</Label>
                            <Input
                                id="validFrom"
                                type="time"
                                value={validFrom}
                                onChange={(e) => setValidFrom(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="validTo">Gültig bis</Label>
                            <Input
                                id="validTo"
                                type="time"
                                value={validTo}
                                onChange={(e) => setValidTo(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Gültig an Tagen *</Label>
                        <div className="flex flex-wrap gap-2">
                            {weekdays.map(day => (
                                <button
                                    key={day.value}
                                    type="button"
                                    onClick={() => toggleDay(day.value)}
                                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${validOnDay.includes(day.value)
                                            ? 'bg-brand-primary text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    {day.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleAdd}
                        disabled={!isFormValid || isSubmitting}
                        className="bg-brand-primary hover:bg-brand-primary/90"
                    >
                        {isSubmitting ? 'Wird hinzugefügt...' : 'Hinzufügen'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
